import React, { createContext, useEffect, useState } from "react";
import InputFilterText from "../inputs/InputFilter/InputFilterText";
import useFilter from "../../hooks/useFilter";
import InputFilterSimpleSelect from "../inputs/InputFilterSimpleSelect/InputFilterSimpleSelect";
import { MultiFilterContainer } from "./MultiFilter.styled";

export const MultiFilterContext = createContext();

export class FilterType {
    static TEXT = "text";
    static TEXT_HYBRID = "text-hybrid";
    static SIMPLE_SELECT = "simple-select";
    static DATE = "date";
}

/**
 * MultiFilter component that allows filtering data by multiple customFilters
 */
const MultiFilter = ({
    customFilters,
    rowsData,
    setRowsData,
    setNotFound, // Temp way to give external component access to filter values
    setFiltersUsed, // Temp way to give external component access to filter values
}) => {
    const [filters, setFilters] = useState({});
    const filteredData = useFilter(rowsData, filters);

    /**
     * Creates an array of filters
     * @param {*} event The event that triggered the filter
     * @param {string} filterType The type of filter that should be applied
     */
    const handleFilterChange = (event, filterType) => {
        const { name, value } = event.target;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: { value, filterType },
        }));
    };

    const cleanFilter = filterToClean => {
        const { name } = filterToClean.current;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: { value: "", filterType: "" },
        }));
    };

    useEffect(() => {
        if (filteredData.length === 0 && Object.keys(filters).length !== 0) {
            let notFoundString = "";
            let columnToFilter;

            Object.keys(filters).forEach((key, _index) => {
                columnToFilter = customFilters.find(customFilter => customFilter.column === key);
                if (!columnToFilter) {
                    return;
                }
                switch (columnToFilter.filterType) {
                    case FilterType.TEXT:
                    case FilterType.TEXT_HYBRID:
                        notFoundString = notFoundString.concat(
                            `${filters[key].value},`
                        );
                        break;
                    case FilterType.SIMPLE_SELECT:
                        let selectedOption = columnToFilter.options.find(
                            option => option.value.toString() === filters[key].value
                        );
                        notFoundString = notFoundString.concat(
                            `${selectedOption.label},`
                        );
                        break;
                    default:
                        throw new Error("Not implemented");
                }
            });
            notFoundString = notFoundString.replace(/,\s*$/, "");
            setNotFound(notFoundString);
        }
    }, [customFilters, filters, setNotFound, filteredData]);

    useEffect(() => {
        setFiltersUsed(filters);
        setRowsData(filteredData);
    }, [filters, filteredData, setFiltersUsed, setRowsData]);

    return (
        <MultiFilterContainer $numFilters={customFilters.length}>
            <MultiFilterContext.Provider
                value={{ filters, handleFilterChange, cleanFilter }}
            >
                {customFilters?.map((customFilter, index) => {
                    switch (customFilter.filterType) {
                        case FilterType.TEXT:
                        case FilterType.TEXT_HYBRID:
                            return (
                                <InputFilterText
                                    key={index}
                                    column={customFilter.column}
                                    placeholder={customFilter.placeholder}
                                    isMultiFilter={true}
                                    filterType={customFilter.filterType}
                                />
                            );
                        case FilterType.SIMPLE_SELECT:
                            return (
                                <InputFilterSimpleSelect
                                    key={index}
                                    column={customFilter.column}
                                    placeholder={customFilter.placeholder}
                                    options={customFilter.options}
                                    isMultiFilter={true}
                                    filterType={customFilter.filterType}
                                />
                            );
                        case FilterType.DATE:
                            return null; // TODO: Implement date filter
                        default:
                            throw new Error("Not implemented");
                    }
                })}
            </MultiFilterContext.Provider>
        </MultiFilterContainer>
    );
};

export default MultiFilter;
